import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FaShoppingCart, FaStar, FaComments, FaSpinner, FaFilePdf, FaDownload, FaTag, FaCog, FaBox, FaCalendarAlt, FaShieldAlt, FaRuler, FaWeight, FaInfoCircle, FaMapMarkerAlt, FaTruck, FaClipboardList, FaCheck, FaTimes, FaChevronDown, FaChevronRight, FaExchangeAlt } from 'react-icons/fa';
import { getCurrencyByLocation, formatPrice } from '../utils/currency';
import { getFallbackImage } from '../utils/fallbackImages';
import QuoteRequestModal from '../components/QuoteRequestModal';
import ReviewForm from '../components/ReviewForm';
import { useComparator } from '../context/ComparatorContext';
import {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link as LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box, Anchor, HardHat, ChevronRight as ChevronRightLucide
} from 'lucide-react';

// Section icons mapping for template display
const SECTION_ICONS = {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link: LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box, Anchor, HardHat
};

export default function ProductDetail() {
  const { id, categorySlug, productSlug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { API, addToCart, user, canPerformActions } = useContext(AppContext);
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedType, setSelectedType] = useState('new');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [existingConversation, setExistingConversation] = useState(null);
  
  // Template state for dynamic specs display
  const [productTemplate, setProductTemplate] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [productCategory, setProductCategory] = useState(null);
  
  // Comparator hook
  const { addProduct: addToComparator, isInComparator, removeProduct: removeFromComparator } = useComparator();

  // Determine product identifier (ID or slug)
  const productIdentifier = productSlug || id;

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [productIdentifier]);

  useEffect(() => {
    if (user && product?.created_by_user_id) {
      checkExistingConversation();
    }
  }, [user, product]);

  const fetchProduct = async () => {
    try {
      let response;
      // Try slug-based URL first, then fall back to ID
      if (productSlug && categorySlug) {
        response = await axios.get(`${API}/products/by-slug/${categorySlug}/${productSlug}`);
      } else {
        response = await axios.get(`${API}/products/${productIdentifier}`);
      }
      setProduct(response.data);
      
      // Set default type based on availability
      if (response.data.price_new && response.data.stock_new > 0) {
        setSelectedType('new');
      } else if (response.data.price_used && response.data.stock_used > 0) {
        setSelectedType('used');
      } else if (response.data.price_rental && response.data.stock_rental > 0) {
        setSelectedType('rental');
      }
      
      // Fetch template for this product's category
      if (response.data.category_id) {
        fetchTemplateForProduct(response.data.category_id);
        fetchCategoryForProduct(response.data.category_id);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  const fetchCategoryForProduct = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/categories`);
      const category = response.data.find(c => c.id === categoryId);
      if (category) {
        setProductCategory(category);
      }
    } catch (error) {
      console.error('Error fetching category:', error);
    }
  };

  const fetchTemplateForProduct = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/templates/by-category/${categoryId}`);
      const template = response.data;
      
      if (template && template.id !== 'default' && template.sections?.length > 0) {
        setProductTemplate(template);
        // Expand all sections by default
        const expanded = {};
        template.sections.forEach(s => { expanded[s.id] = true; });
        setExpandedSections(expanded);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      // Use product ID once we have it, or wait for product to load
      const prodId = product?.id || productIdentifier;
      const response = await axios.get(`${API}/reviews?product_id=${prodId}&status=approved`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const checkExistingConversation = async () => {
    try {
      const prodId = product?.id || productIdentifier;
      const response = await axios.get(`${API}/products/${prodId}/conversations?user_id=${user.id}`);
      if (response.data && response.data.length > 0) {
        setExistingConversation(response.data[0]);
      }
    } catch (error) {
      // No conversation exists yet
    }
  };

  const handleContactSeller = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Check if account is approved
    if (!canPerformActions || (canPerformActions && !canPerformActions())) {
      toast.error('Votre compte est en attente d\'approbation. Vous ne pouvez pas encore contacter les vendeurs.');
      return;
    }
    
    const prodId = product?.id || productIdentifier;
    // If it's an individual seller, open chat dialog
    if (product?.created_by_user_id && product?.created_by_user_id !== user.id) {
      if (existingConversation) {
        // Go to existing conversation
        navigate(`/product/${prodId}/conversation/${existingConversation.id}`);
      } else {
        // Open dialog to start new conversation
        setShowChatDialog(true);
      }
    } else {
      // For regular products/shops, use quote request modal
      setShowQuoteModal(true);
    }
  };

  const handleStartChat = async () => {
    if (!chatMessage.trim()) {
      toast.error('Veuillez entrer un message');
      return;
    }
    
    setSendingChat(true);
    try {
      const prodId = product?.id || productIdentifier;
      const response = await axios.post(
        `${API}/products/${prodId}/conversations/start?user_id=${user.id}`,
        { message: chatMessage }
      );
      
      toast.success('Message envoyé ! Le vendeur a été notifié.');
      setShowChatDialog(false);
      setChatMessage('');
      
      // Navigate to conversation
      navigate(`/product/${prodId}/conversation/${response.data.conversation_id}`);
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingChat(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    const stock = selectedType === 'new' ? product.stock_new :
                  selectedType === 'used' ? product.stock_used :
                  product.stock_rental;
    
    if (stock <= 0) {
      toast.error(t('product.outOfStock'));
      return;
    }
    
    addToCart(product, selectedType, quantity);
    toast.success(`${i18n.language === 'fr' ? product.name_fr : product.name_en} ${t('product.addToCart')}`);
  };

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  const getCurrentPrice = () => {
    return selectedType === 'new' ? product.price_new :
           selectedType === 'used' ? product.price_used :
           product.price_rental;
  };

  const getCurrentStock = () => {
    return selectedType === 'new' ? product.stock_new :
           selectedType === 'used' ? product.stock_used :
           product.stock_rental;
  };

  // Check if product has any details to show
  const hasSpecs = product.technical_specs || product.brand || product.model || product.year || 
                   product.weight || product.dimensions || product.condition_details || product.warranty;
  const hasAccessories = product.included_accessories && product.included_accessories.length > 0;
  const hasDocuments = product.documents && product.documents.length > 0;
  const hasRentalConditions = product.rental_conditions && product.price_rental;
  
  // Check if product has template fields to display
  const hasTemplateFields = productTemplate && 
    product.template_fields && 
    Object.keys(product.template_fields).length > 0;
  
  // Helper function to render template field value
  const renderFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') return null;
    
    switch (field.field_type) {
      case 'boolean':
        return (
          <span className={`flex items-center gap-1 ${value === true || value === 'true' ? 'text-green-600' : 'text-gray-400'}`}>
            {value === true || value === 'true' ? <FaCheck /> : <FaTimes />}
            {value === true || value === 'true' ? (i18n.language === 'fr' ? 'Oui' : 'Yes') : (i18n.language === 'fr' ? 'Non' : 'No')}
          </span>
        );
      case 'multiselect':
        if (Array.isArray(value)) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((v, idx) => (
                <Badge key={idx} variant="secondary" className="bg-[#635BFF]/10 text-[#635BFF] text-xs">
                  {v}
                </Badge>
              ))}
            </div>
          );
        }
        return value;
      case 'number':
        return (
          <span>
            {value}
            {field.unit && <span className="text-gray-500 ml-1">{field.unit}</span>}
          </span>
        );
      default:
        return value;
    }
  };

  // Build SEO data
  const productName = product?.name_fr || product?.name_en || 'Produit';
  const productDesc = product?.description_fr || product?.description_en || '';
  const seoCurrency = product ? getCurrencyByLocation(product.location) : 'EUR';
  const mainImage = product?.images?.[0] || getFallbackImage(productName);
  const categoryName = productCategory?.name_fr || 'Matériel';
  const seoCategorySlug = productCategory?.slug || product?.category_slug || categorySlug || 'materiel';
  
  // Get main price
  const getMainPrice = () => {
    if (!product) return null;
    if (product.show_price === false) return null;
    if (product.price_used) return product.price_used;
    if (product.price_new) return product.price_new;
    if (product.price_rental) return product.price_rental;
    return null;
  };
  const mainPrice = getMainPrice();

  // Calculate average rating
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  // Schema.org Product JSON-LD
  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productName,
    "description": productDesc.slice(0, 500),
    "image": product.images?.filter(img => img) || [mainImage],
    "brand": product.brand ? { "@type": "Brand", "name": product.brand } : undefined,
    "model": product.model || undefined,
    "sku": product.id,
    "mpn": product.id,
    "category": categoryName,
    "offers": mainPrice ? {
      "@type": "Offer",
      "url": `https://geots.consulting/product/${product.id}`,
      "price": mainPrice,
      "priceCurrency": "EUR",
      "availability": product.availability_status === 'available' 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "seller": {
        "@type": "Organization",
        "name": "GEOTS Consulting"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "FR"
        }
      }
    } : {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": 0,
      "priceCurrency": "EUR"
    },
    "aggregateRating": avgRating && reviews.length > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": avgRating,
      "reviewCount": reviews.length,
      "bestRating": "5",
      "worstRating": "1"
    } : undefined,
    "review": reviews.length > 0 ? reviews.slice(0, 5).map(r => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating,
        "bestRating": "5"
      },
      "author": { "@type": "Person", "name": r.author_name || "Client vérifié" },
      "reviewBody": r.comment
    })) : undefined
  } : null;

  // Breadcrumb JSON-LD
  const breadcrumbSchema = product ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://geots.consulting/" },
      { "@type": "ListItem", "position": 2, "name": "Matériel", "item": "https://geots.consulting/shop" },
      { "@type": "ListItem", "position": 3, "name": categoryName, "item": `https://geots.consulting/materiel/${seoCategorySlug}` },
      { "@type": "ListItem", "position": 4, "name": productName, "item": `https://geots.consulting/product/${product.id}` }
    ]
  } : null;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="product-detail-page">
      {/* SEO Meta Tags & Schema.org */}
      {product && (
        <Helmet>
          <title>{`${productName}${product.brand ? ` | ${product.brand}` : ''} | GEOTS Consulting`}</title>
          <meta 
            name="description" 
            content={`${product.brand || ''} ${productName}${product.year ? ` ${product.year}` : ''} - ${productDesc.slice(0, 150)}${productDesc.length > 150 ? '...' : ''}`.trim()} 
          />
          <link rel="canonical" href={`https://geots.consulting/product/${product.id}`} />
          
          {/* Open Graph */}
          <meta property="og:title" content={`${productName} | GEOTS`} />
          <meta property="og:description" content={productDesc.slice(0, 200)} />
          <meta property="og:type" content="product" />
          <meta property="og:url" content={`https://geots.consulting/product/${product.id}`} />
          <meta property="og:image" content={mainImage} />
          {mainPrice && (
            <>
              <meta property="product:price:amount" content={mainPrice.toString()} />
              <meta property="product:price:currency" content="EUR" />
            </>
          )}
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={productName} />
          <meta name="twitter:description" content={productDesc.slice(0, 200)} />
          <meta name="twitter:image" content={mainImage} />
          
          {/* Schema.org JSON-LD */}
          <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        {product && (
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
            <ChevronRightLucide className="w-4 h-4" />
            <Link to="/shop" className="hover:text-[#635BFF]">Matériel</Link>
            {productCategory && (
              <>
                <ChevronRightLucide className="w-4 h-4" />
                <Link to={`/materiel/${seoCategorySlug}`} className="hover:text-[#635BFF]">{categoryName}</Link>
              </>
            )}
            <ChevronRightLucide className="w-4 h-4" />
            <span className="text-[#0A2540] font-medium truncate max-w-[200px]">{productName}</span>
          </nav>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Images Gallery */}
          <div>
            <Card className="sticky top-4">
              <CardContent className="p-4">
                {(() => {
                  // Filter out empty/null images
                  const validImages = (product.images || []).filter(img => img && img.trim() !== '');
                  const fallbackImg = getFallbackImage(i18n.language === 'fr' ? product.name_fr : product.name_en);
                  
                  if (validImages.length > 0) {
                    return (
                  <div>
                    {/* Main Image */}
                    <div className="relative mb-4">
                      <img 
                        src={validImages[currentImageIndex] || validImages[0]} 
                        alt={`${i18n.language === 'fr' ? product.name_fr : product.name_en} - Image ${currentImageIndex + 1}`}
                        className="w-full h-96 object-cover rounded-lg"
                        data-testid="product-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackImg;
                        }}
                      />
                      {validImages.length > 1 && (
                        <>
                          {/* Previous Button */}
                          <button
                            onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1))}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                            aria-label="Image précédente"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          {/* Next Button */}
                          <button
                            onClick={() => setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1))}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                            aria-label="Image suivante"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Thumbnails */}
                    {validImages.length > 1 && (
                      <div className="grid grid-cols-4 gap-2">
                        {validImages.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`border-2 rounded-lg overflow-hidden ${
                              currentImageIndex === index ? 'border-[#0A2540]' : 'border-gray-200'
                            }`}
                          >
                            <img 
                              src={image} 
                              alt={`Miniature ${index + 1}`}
                              className="w-full h-20 object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = fallbackImg;
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                    );
                  } else {
                    return (
                  <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
                    <img 
                      src={fallbackImg}
                      alt={i18n.language === 'fr' ? product.name_fr : product.name_en}
                      className="w-full h-96 object-cover rounded-lg"
                    />
                  </div>
                    );
                  }
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Product Info */}
          <div>
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {product.brand && (
                <Badge variant="secondary" className="bg-[#635BFF]/10 text-[#635BFF] border-[#635BFF]/20">
                  <FaTag className="mr-1 text-xs" /> {product.brand}
                </Badge>
              )}
              {product.model && (
                <Badge variant="secondary" className="bg-[#0A2540]/10 text-[#0A2540] border-[#0A2540]/20">
                  <FaCog className="mr-1 text-xs" /> {product.model}
                </Badge>
              )}
              {product.year && (
                <Badge variant="secondary" className="bg-[#00D4FF]/10 text-[#00D4FF] border-[#00D4FF]/20">
                  <FaCalendarAlt className="mr-1 text-xs" /> {product.year}
                </Badge>
              )}
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold mb-4 text-[#0A2540]" data-testid="product-name">
              {i18n.language === 'fr' ? product.name_fr : product.name_en}
            </h1>
            
            <p className="text-gray-600 mb-6 text-base leading-relaxed" data-testid="product-description">
              {i18n.language === 'fr' ? product.description_fr : product.description_en}
            </p>

            {/* Quick Info Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Availability Status */}
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${product.availability_status === 'available' ? 'bg-green-500' : product.availability_status === 'on_order' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                  <span className={`font-medium text-sm ${product.availability_status === 'out_of_stock' ? 'text-red-500' : 'text-gray-700'}`} data-testid="product-availability">
                    {i18n.language === 'fr' ? (
                      product.availability_status === 'available' ? 'Disponible' :
                      product.availability_status === 'on_order' ? 'Sur commande' :
                      'Rupture de stock'
                    ) : (
                      product.availability_status === 'available' ? 'Available' :
                      product.availability_status === 'on_order' ? 'On order' :
                      'Out of stock'
                    )}
                  </span>
                </div>
              </div>

              {/* Delivery Time */}
              {product.delivery_time && (
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <FaTruck className="text-[#635BFF]" />
                    <span className="text-sm text-gray-700" data-testid="product-delivery">
                      {product.delivery_time}
                    </span>
                  </div>
                </div>
              )}

              {/* Location */}
              {product.location && (
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="text-[#00D4FF]" />
                    <span className="text-sm text-gray-700">{product.location}</span>
                  </div>
                </div>
              )}

              {/* Warranty */}
              {product.warranty && (
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <FaShieldAlt className="text-green-600" />
                    <span className="text-sm text-gray-700">{product.warranty}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Type Selection */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3 text-[#0A2540]">{i18n.language === 'fr' ? 'Options d\'achat' : 'Purchase Options'}</h3>
              <div className="flex flex-wrap gap-3">
                {product.price_new && (
                  <Button
                    variant={selectedType === 'new' ? 'default' : 'outline'}
                    onClick={() => setSelectedType('new')}
                    className={selectedType === 'new' ? 'bg-[#0A2540]' : ''}
                    data-testid="type-new"
                  >
                    {t('product.new')} {product.show_price && `- ${formatPrice(product.price_new, seoCurrency)} HT`}
                  </Button>
                )}
                {product.price_used && (
                  <Button
                    variant={selectedType === 'used' ? 'default' : 'outline'}
                    onClick={() => setSelectedType('used')}
                    className={selectedType === 'used' ? 'bg-[#635BFF]' : ''}
                    data-testid="type-used"
                  >
                    {t('product.used')} {product.show_price && `- ${formatPrice(product.price_used, seoCurrency)} HT`}
                  </Button>
                )}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <label className="block font-semibold mb-2 text-[#0A2540]">{i18n.language === 'fr' ? 'Quantité' : 'Quantity'}</label>
              <input
                type="number"
                min="1"
                max={getCurrentStock()}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                data-testid="quantity-input"
              />
            </div>

            {/* Price */}
            <div className="mb-6 p-4 bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl border border-[#635BFF]/10">
              {product.show_price ? (
                <div>
                  <span className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Prix total HT' : 'Total price excl. tax'}</span>
                  <div className="text-3xl font-bold text-[#0A2540]" data-testid="product-price">
                    {formatPrice(getCurrentPrice() * quantity, seoCurrency)} <span className="text-base font-normal text-gray-500">HT</span>
                  </div>
                </div>
              ) : (
                <div className="text-2xl font-semibold text-[#0A2540]" data-testid="product-price-on-request">
                  {i18n.language === 'fr' ? 'Prix sur demande' : 'Price on request'}
                </div>
              )}
            </div>

            {/* Add to Cart or Request Quote */}
            <div className="flex flex-wrap gap-3">
              {product.show_price ? (
                <Button 
                  onClick={() => user ? handleAddToCart() : navigate('/login')}
                  disabled={getCurrentStock() <= 0}
                  className="flex-1 md:flex-none bg-[#0A2540] hover:bg-[#003d56] text-lg py-6"
                  data-testid="add-to-cart-btn"
                >
                  <FaShoppingCart className="mr-2" />
                  {t('product.addToCart')}
                </Button>
              ) : (
                <Button 
                  onClick={() => user ? setShowQuoteModal(true) : navigate('/login')}
                  className="flex-1 md:flex-none bg-[#635BFF] hover:bg-[#5248d9] text-lg py-6"
                  data-testid="request-quote-btn"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {i18n.language === 'fr' ? 'Demander un devis' : 'Request a quote'}
                </Button>
              )}
              
              {/* Rent Button - redirects to rental form or login */}
              {product.price_rental && product.stock_rental > 0 && (
                <Button 
                  onClick={() => user ? navigate(`/rental/${product.id}`) : navigate('/login')}
                  className="flex-1 md:flex-none bg-[#00D4FF] hover:bg-[#00B4D8] text-[#0A2540] text-lg py-6"
                  data-testid="rent-btn"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {i18n.language === 'fr' ? 'Louer cet équipement' : 'Rent this equipment'}
                </Button>
              )}
              
              {/* Contact Seller Button - always visible */}
              <Button 
                onClick={handleContactSeller}
                variant="outline"
                className="flex-1 md:flex-none border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white text-lg py-6"
                data-testid="contact-seller-btn"
              >
                <FaComments className="mr-2" />
                {existingConversation 
                  ? (i18n.language === 'fr' ? 'Voir la conversation' : 'View conversation')
                  : (i18n.language === 'fr' ? 'Contacter le vendeur' : 'Contact seller')}
              </Button>
              
              {/* Compare Button */}
              {productCategory && (
                <Button 
                  onClick={() => {
                    if (isInComparator(product.id)) {
                      removeFromComparator(product.id);
                    } else {
                      addToComparator(product, productCategory);
                    }
                  }}
                  variant={isInComparator(product.id) ? "default" : "outline"}
                  className={`flex-1 md:flex-none text-lg py-6 ${
                    isInComparator(product.id) 
                      ? 'bg-[#635BFF] hover:bg-[#5248d9] text-white' 
                      : 'border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF] hover:text-white'
                  }`}
                  data-testid="compare-btn"
                >
                  <FaExchangeAlt className="mr-2" />
                  {isInComparator(product.id)
                    ? (i18n.language === 'fr' ? 'Dans le comparateur' : 'In comparator')
                    : (i18n.language === 'fr' ? '+ Comparer' : '+ Compare')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Detailed Information Tabs */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <Tabs defaultValue={hasTemplateFields ? "techsheet" : "details"} className="w-full">
              <TabsList className={`grid w-full mb-6 ${hasTemplateFields ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'}`}>
                {hasTemplateFields && (
                  <TabsTrigger value="techsheet" className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white">
                    <FaClipboardList className="mr-2" />
                    {i18n.language === 'fr' ? 'Fiche technique' : 'Tech Sheet'}
                  </TabsTrigger>
                )}
                <TabsTrigger value="details" className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white">
                  <FaInfoCircle className="mr-2" />
                  {i18n.language === 'fr' ? 'Détails' : 'Details'}
                </TabsTrigger>
                <TabsTrigger value="specs" className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white">
                  <FaCog className="mr-2" />
                  {i18n.language === 'fr' ? 'Spécifications' : 'Specifications'}
                </TabsTrigger>
                {hasDocuments && (
                  <TabsTrigger value="documents" className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white">
                    <FaFilePdf className="mr-2" />
                    {i18n.language === 'fr' ? 'Documents' : 'Documents'}
                  </TabsTrigger>
                )}
                {hasRentalConditions && (
                  <TabsTrigger value="rental" className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white">
                    <FaCalendarAlt className="mr-2" />
                    {i18n.language === 'fr' ? 'Location' : 'Rental'}
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Tech Sheet Tab - Dynamic Template Fields */}
              {hasTemplateFields && (
                <TabsContent value="techsheet" className="mt-0">
                  <div className="space-y-4">
                    {/* Template Info Header */}
                    <div className="flex items-center gap-3 p-3 bg-[#635BFF]/5 rounded-lg border border-[#635BFF]/20">
                      <FaClipboardList className="text-[#635BFF]" />
                      <div>
                        <p className="font-medium text-[#0A2540]">
                          {i18n.language === 'fr' ? productTemplate.name_fr : productTemplate.name_en}
                        </p>
                        <p className="text-xs text-gray-500">
                          {i18n.language === 'fr' ? 'Fiche technique normalisée GEOTS' : 'GEOTS standardized specification sheet'}
                        </p>
                      </div>
                    </div>

                    {/* Dynamic Sections */}
                    {(productTemplate.sections || [])
                      .sort((a, b) => (a.position || 0) - (b.position || 0))
                      .map((section) => {
                        const SectionIcon = SECTION_ICONS[section.icon_lucide] || Box;
                        const sectionFields = (productTemplate.fields || [])
                          .filter(f => f.section_id === section.id && f.visible !== false)
                          .sort((a, b) => (a.position || 0) - (b.position || 0));
                        
                        // Only show sections with filled fields
                        const filledFields = sectionFields.filter(f => {
                          const value = product.template_fields?.[f.name];
                          return value !== null && value !== undefined && value !== '' && 
                                 !(Array.isArray(value) && value.length === 0);
                        });
                        
                        if (filledFields.length === 0) return null;
                        
                        return (
                          <div key={section.id} className="border rounded-lg overflow-hidden">
                            {/* Section Header */}
                            <button
                              onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#635BFF]/10 rounded-lg flex items-center justify-center">
                                  <SectionIcon size={20} className="text-[#635BFF]" />
                                </div>
                                <div className="text-left">
                                  <h3 className="font-semibold text-[#0A2540]">
                                    {i18n.language === 'fr' ? section.name_fr : section.name_en}
                                  </h3>
                                  <span className="text-xs text-gray-500">{filledFields.length} {i18n.language === 'fr' ? 'caractéristiques' : 'specifications'}</span>
                                </div>
                              </div>
                              {expandedSections[section.id] ? (
                                <FaChevronDown className="text-gray-400" />
                              ) : (
                                <FaChevronRight className="text-gray-400" />
                              )}
                            </button>
                            
                            {/* Section Content */}
                            {expandedSections[section.id] && (
                              <div className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {filledFields.map((field) => {
                                    const value = product.template_fields?.[field.name];
                                    const label = i18n.language === 'fr' ? field.label_fr : (field.label_en || field.label_fr);
                                    
                                    return (
                                      <div 
                                        key={field.id} 
                                        className={`p-3 bg-gray-50 rounded-lg ${field.field_type === 'multiselect' || field.field_type === 'textarea' ? 'md:col-span-2' : ''}`}
                                      >
                                        <div className="text-xs text-gray-500 mb-1">{label}</div>
                                        <div className="font-medium text-[#0A2540]">
                                          {renderFieldValue(field, value)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </TabsContent>
              )}

              {/* Details Tab */}
              <TabsContent value="details" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Product Info */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#0A2540] border-b pb-2">
                      {i18n.language === 'fr' ? 'Informations produit' : 'Product Information'}
                    </h3>
                    
                    {product.brand && (
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center"><FaTag className="mr-2 text-[#635BFF]" /> {i18n.language === 'fr' ? 'Marque' : 'Brand'}</span>
                        <span className="font-medium text-[#0A2540]">{product.brand}</span>
                      </div>
                    )}
                    
                    {product.model && (
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center"><FaCog className="mr-2 text-[#635BFF]" /> {i18n.language === 'fr' ? 'Modèle' : 'Model'}</span>
                        <span className="font-medium text-[#0A2540]">{product.model}</span>
                      </div>
                    )}
                    
                    {product.year && (
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center"><FaCalendarAlt className="mr-2 text-[#635BFF]" /> {i18n.language === 'fr' ? 'Année' : 'Year'}</span>
                        <span className="font-medium text-[#0A2540]">{product.year}</span>
                      </div>
                    )}

                    {product.condition_details && (
                      <div className="py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center mb-2"><FaBox className="mr-2 text-[#635BFF]" /> {i18n.language === 'fr' ? 'Description détaillée' : 'Detailed Description'}</span>
                        <p className="text-gray-700 text-sm leading-relaxed bg-gray-50 p-3 rounded-lg">{product.condition_details}</p>
                      </div>
                    )}

                    {product.warranty && (
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center"><FaShieldAlt className="mr-2 text-green-600" /> {i18n.language === 'fr' ? 'Garantie' : 'Warranty'}</span>
                        <span className="font-medium text-[#0A2540]">{product.warranty}</span>
                      </div>
                    )}

                    {!product.brand && !product.model && !product.year && !product.condition_details && !product.warranty && (
                      <p className="text-gray-500 italic py-4">
                        {i18n.language === 'fr' ? 'Aucune information supplémentaire disponible.' : 'No additional information available.'}
                      </p>
                    )}
                  </div>

                  {/* Physical Specs & Accessories */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#0A2540] border-b pb-2">
                      {i18n.language === 'fr' ? 'Caractéristiques physiques' : 'Physical Characteristics'}
                    </h3>
                    
                    {product.weight && (
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center"><FaWeight className="mr-2 text-[#00D4FF]" /> {i18n.language === 'fr' ? 'Poids' : 'Weight'}</span>
                        <span className="font-medium text-[#0A2540]">{product.weight}</span>
                      </div>
                    )}
                    
                    {product.dimensions && (
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600 flex items-center"><FaRuler className="mr-2 text-[#00D4FF]" /> {i18n.language === 'fr' ? 'Dimensions' : 'Dimensions'}</span>
                        <span className="font-medium text-[#0A2540]">{product.dimensions}</span>
                      </div>
                    )}

                    {hasAccessories && (
                      <div className="pt-4">
                        <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center">
                          <FaBox className="mr-2 text-[#635BFF]" />
                          {i18n.language === 'fr' ? 'Accessoires inclus' : 'Included Accessories'}
                        </h4>
                        <ul className="space-y-2">
                          {product.included_accessories.map((accessory, index) => (
                            <li key={index} className="flex items-center text-gray-700">
                              <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {accessory}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!product.weight && !product.dimensions && !hasAccessories && (
                      <p className="text-gray-500 italic py-4">
                        {i18n.language === 'fr' ? 'Aucune caractéristique physique renseignée.' : 'No physical characteristics available.'}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Specifications Tab */}
              <TabsContent value="specs" className="mt-0">
                {product.technical_specs ? (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-bold text-lg text-[#0A2540] mb-4 flex items-center">
                      <FaCog className="mr-2 text-[#635BFF]" />
                      {i18n.language === 'fr' ? 'Spécifications techniques' : 'Technical Specifications'}
                    </h3>
                    <div className="prose prose-sm max-w-none text-gray-700">
                      {/* Parse specs and extract PDF links */}
                      {product.technical_specs.split('\n').map((line, index) => {
                        const trimmedLine = line.trim();
                        // Check if line is a URL (PDF or other)
                        if (trimmedLine.match(/^https?:\/\/.+\.(pdf|doc|docx)$/i)) {
                          return (
                            <a 
                              key={index}
                              href={trimmedLine}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center p-3 mt-4 bg-white border border-gray-200 rounded-lg hover:border-[#635BFF] hover:shadow-md transition-all group no-underline"
                            >
                              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-red-200 transition-colors">
                                <FaFilePdf className="text-red-500" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-[#0A2540] group-hover:text-[#635BFF] text-sm">
                                  {i18n.language === 'fr' ? 'Fiche technique PDF' : 'Technical Sheet PDF'}
                                </p>
                                <p className="text-xs text-gray-500 truncate max-w-xs">{trimmedLine.split('/').pop()}</p>
                              </div>
                              <FaDownload className="text-gray-400 group-hover:text-[#635BFF]" />
                            </a>
                          );
                        }
                        // Check if line contains a URL
                        if (trimmedLine.match(/https?:\/\/[^\s]+/)) {
                          const urlMatch = trimmedLine.match(/https?:\/\/[^\s]+/);
                          const url = urlMatch ? urlMatch[0] : '';
                          const textBefore = trimmedLine.replace(url, '').trim();
                          return (
                            <div key={index} className="mb-1">
                              {textBefore && <span>{textBefore} </span>}
                              <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#635BFF] hover:underline break-all">
                                {url}
                              </a>
                            </div>
                          );
                        }
                        // Regular spec line
                        if (trimmedLine) {
                          return <div key={index} className="mb-1">{trimmedLine}</div>;
                        }
                        return null;
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FaCog className="mx-auto text-4xl mb-4 text-gray-300" />
                    <p>{i18n.language === 'fr' ? 'Aucune spécification technique disponible.' : 'No technical specifications available.'}</p>
                    <p className="text-sm mt-2">{i18n.language === 'fr' ? 'Contactez le vendeur pour plus d\'informations.' : 'Contact the seller for more information.'}</p>
                  </div>
                )}
              </TabsContent>

              {/* Documents Tab */}
              {hasDocuments && (
                <TabsContent value="documents" className="mt-0">
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-[#0A2540] mb-4 flex items-center">
                      <FaFilePdf className="mr-2 text-red-500" />
                      {i18n.language === 'fr' ? 'Documents téléchargeables' : 'Downloadable Documents'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {product.documents.map((doc, index) => (
                        <a 
                          key={index}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center p-4 bg-white border border-gray-200 rounded-lg hover:border-[#635BFF] hover:shadow-md transition-all group"
                        >
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-red-200 transition-colors">
                            <FaFilePdf className="text-red-500 text-xl" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-[#0A2540] group-hover:text-[#635BFF]">{doc.name || `Document ${index + 1}`}</p>
                            <p className="text-sm text-gray-500">{doc.type || 'PDF'}</p>
                          </div>
                          <FaDownload className="text-gray-400 group-hover:text-[#635BFF]" />
                        </a>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              )}

              {/* Rental Conditions Tab */}
              {hasRentalConditions && (
                <TabsContent value="rental" className="mt-0">
                  <div className="bg-gradient-to-r from-[#00D4FF]/5 to-[#635BFF]/5 rounded-lg p-6">
                    <h3 className="font-bold text-lg text-[#0A2540] mb-4 flex items-center">
                      <FaCalendarAlt className="mr-2 text-[#00D4FF]" />
                      {i18n.language === 'fr' ? 'Conditions de location' : 'Rental Conditions'}
                    </h3>
                    <div className="mb-4 p-4 bg-white rounded-lg border border-[#00D4FF]/20">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">{i18n.language === 'fr' ? 'Tarif journalier HT' : 'Daily Rate excl. tax'}</span>
                        {product.show_price !== false ? (
                          <span className="text-2xl font-bold text-[#00D4FF]">{formatPrice(product.price_rental, seoCurrency)} <span className="text-sm font-normal">HT</span>/{i18n.language === 'fr' ? 'jour' : 'day'}</span>
                        ) : (
                          <span className="text-xl font-semibold text-[#635BFF]">{i18n.language === 'fr' ? 'Prix sur demande' : 'Price on request'}</span>
                        )}
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {product.rental_conditions}
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>

        {/* Quote Request Modal */}
        <QuoteRequestModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          product={product}
          selectedType={selectedType}
          quantity={quantity}
          API={API}
        />

        {/* Review Form */}
        {user && (
          <div className="mb-8">
            <ReviewForm 
              product={product} 
              user={user} 
              API={API}
              onReviewSubmitted={fetchReviews}
            />
          </div>
        )}

        {/* Reviews */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-[#0A2540]">{t('product.reviews')}</h2>
            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map(review => (
                  <div key={review.id} className="border-b pb-4" data-testid={`review-${review.id}`}>
                    <div className="flex items-center mb-2">
                      <span className="font-semibold mr-3">{review.user_name}</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <FaStar key={i} className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'} />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600">{review.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Aucun avis pour le moment</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Chat Dialog for Individual Sellers */}
      <Dialog open={showChatDialog} onOpenChange={setShowChatDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>💬 Contacter le vendeur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Vous contactez le vendeur pour :</p>
              <p className="font-bold text-[#0A2540]">{product?.name_fr}</p>
              {product?.show_price !== false ? (
                <>
                  {product?.price_rental && (
                    <p className="text-[#00D4FF] font-medium">{product.price_rental} €/jour</p>
                  )}
                  {product?.price_new && (
                    <p className="text-[#635BFF] font-medium">{product.price_new} €</p>
                  )}
                </>
              ) : (
                <p className="text-[#635BFF] font-medium">{i18n.language === 'fr' ? 'Prix sur demande' : 'Price on request'}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="chat-message">Votre message</Label>
              <Textarea
                id="chat-message"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Bonjour, je suis intéressé(e) par votre annonce..."
                rows={4}
                className="mt-2"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowChatDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleStartChat} 
                disabled={sendingChat || !chatMessage.trim()}
                className="bg-[#0A2540]"
              >
                {sendingChat ? <FaSpinner className="animate-spin mr-2" /> : <FaComments className="mr-2" />}
                Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}